import { CustomerType, DiscountType } from '@repo/common';
import { Button, Card, CardContent, cn } from '@repo/shadcn';
import { useDebounce } from '@uidotdev/usehooks';
import { format } from 'date-fns';
import { Loader2Icon } from 'lucide-react';
import { useRef, useState } from 'react';
import { z } from 'zod';

import { IwForm, IwFormRef } from '@/components/form';
import { SelectField, TextField } from '@/components/form/fields';
import { AutoCompleteInput } from '@/components/ui/autocomplete/auto-complete-input';
import { useGetCartStocksQuery } from '@/features/slice/inventory';
import { OrderTypeSelector } from '@/modules/cart/order-type-selector.component';
import { formatCurrencyInBDT, toastError } from '@/utils';

import { useCartContext } from './cart.context';

const defaultValue = { label: '', value: '' };

const AddProductFormSchema = z.object({
    purchase_id: z.string().optional(),
    quantity: z.coerce
        .number()
        .min(1, { message: 'Quantity must be at least 1' })
        .optional(),
    price: z.coerce
        .number()
        .min(0, { message: 'Price must be at least 0' })
        .optional(),
    discount_type: z
        .enum([DiscountType.Fixed, DiscountType.Percentage])
        .optional(),
    discount_value: z.coerce
        .number()
        .min(0, { message: 'Discount must be at least 0' })
        .optional(),
    strength: z.string().optional().nullable(),
    type: z.string().optional().nullable(),
    unit: z.string().optional().nullable(),
    expiry_date: z.string().optional().nullable(),
});

type AddProductFormType = z.infer<typeof AddProductFormSchema>;

const initialValue: Partial<AddProductFormType> = {
    // quantity: '',
    // price: 0,
    discount_type: DiscountType.Fixed,
    quantity: undefined,
    price: undefined,
    discount_value: undefined,
};

export const ProductSelector = () => {
    const [searchInputText, setSearchInputText] = useState('');
    const [availableQuantity, setAvailableQuantity] = useState<number | null>(
        null,
    );
    const debouncedSearchText = useDebounce(searchInputText, 100);

    const formRef = useRef<IwFormRef<AddProductFormType>>(null);
    const quantityInputRef = useRef<HTMLInputElement>(null);
    const productAutoCompleteRef = useRef<HTMLButtonElement>(null);

    // const [initialValue, setInitialValue] = useState<AddProductFormType>();

    const { orderType, addItemToCart, isAddItemToCartLoading } =
        useCartContext();

    const {
        data: productList,
        isLoading,
        isFetching,
    } = useGetCartStocksQuery({
        // limit: 10,
        // page: 1,
        search: debouncedSearchText,
    });

    const options = productList?.data.map((product: any) => ({
        label: `${product.name}`,
        value: `${product.purchase_item_id}`,
        quantity: product.quantity,
        expiry_date: product.expiration_date,
        type: product.type,
        retail_price: product.retail_price,
        wholesale_price: product.wholesale_price,
        brand: product.brand || '',
        strength: product.strength,
        name: product.name,
        product_name: product.name,
        unit: product.unit,
    }));

    const onValueChange = (value: { label: string; value: string }) => {
        const isCleared = value.value === '' && value.label === '';

        if (isCleared) {
            formRef.current?.setValue('price', '');
            formRef.current?.setValue('quantity', '');
            formRef.current?.setValue('discount_type', DiscountType.Fixed);
            formRef.current?.setValue('discount_value', '');
            formRef.current?.setValue('purchase_id', '');

            formRef.current?.setValue('strength', '');
            formRef.current?.setValue('type', '');
            formRef.current?.setValue('unit', '');
            formRef.current?.setValue('expiry_date', '');

            setAvailableQuantity(null);
            return;
        }

        const price =
            orderType === CustomerType.RETAIL
                ? (value as any).retail_price
                : (value as any).wholesale_price;

        formRef.current?.setValue('price', price);
        formRef.current?.setValue('quantity', '');
        formRef.current?.setValue('discount_type', DiscountType.Fixed);
        formRef.current?.setValue('discount_value', '');
        formRef.current?.setValue('purchase_id', value.value);

        formRef.current?.setValue('strength', (value as any)?.strength);
        formRef.current?.setValue('type', (value as any)?.type);
        formRef.current?.setValue('unit', (value as any)?.unit);
        formRef.current?.setValue('expiry_date', (value as any)?.expiry_date);

        setAvailableQuantity((value as any).quantity || null);

        setTimeout(() => {
            quantityInputRef.current?.focus();
        }, 0);
    };

    const onInputChange = (input: string) => {
        setSearchInputText(input);
    };

    const handleAddProductToCart = async (data: AddProductFormType) => {
        try {
            await addItemToCart({
                purchase_id: data.purchase_id!,
                quantity: data.quantity!,
                price: data.price!,
                discount_type: data.discount_type!,
                discount_value: data.discount_value!,
            }).unwrap();
            setTimeout(() => {
                productAutoCompleteRef.current?.click();
            }, 0);
            setAvailableQuantity(null);
            formRef.current?.setValue('discount_type', DiscountType.Fixed);
            return;
        } catch (error) {
            toastError(error, 'ProductSelector:handleAddProductToCart');
            return;
        }
    };

    return (
        <Card>
            {/*<CardHeader className="px-3 py-3">*/}
            {/*<CardTitle>Select Product</CardTitle>*/}
            {/*</CardHeader>*/}
            <CardContent className="px-3 pb-2 pt-3">
                <div className={'relative mb-4 grid grid-cols-[2fr_1fr] gap-2'}>
                    <AutoCompleteInput
                        ref={productAutoCompleteRef}
                        options={options || []}
                        emptyMessage="No match found!"
                        onValueChange={onValueChange}
                        placeholder={'Select product'}
                        value={defaultValue}
                        onInputChange={onInputChange}
                        isLoading={isLoading || isFetching}
                        renderOption={(option) => {
                            const name = `${option.name} ${option.unit}`;
                            // const brand = (option as any).brand;
                            const quantity = (option as any).quantity;
                            const retailPrice = (option as any).retail_price;
                            const expiryDate = (option as any).expiry_date;
                            const wholesalePrice = (option as any)
                                .wholesale_price;

                            const price =
                                orderType === CustomerType.RETAIL
                                    ? retailPrice
                                    : wholesalePrice;

                            return (
                                <div className="flex w-full items-center justify-between space-y-1">
                                    <span>
                                        {name} {formatCurrencyInBDT(price)} (
                                        {new Date(
                                            expiryDate,
                                        ).toLocaleDateString()}
                                        )
                                    </span>
                                    <span>Qty- {quantity}</span>
                                </div>
                            );
                        }}
                    />

                    <div
                        className={cn(
                            'absolute left-4 right-0 top-full flex flex-wrap gap-4 text-xs',
                            !formRef.current?.getValues().purchase_id &&
                                'hidden',
                        )}
                    >
                        <div className="space-x-0.5">
                            <span>Strength:</span>
                            <span>{formRef.current?.getValues().strength}</span>
                        </div>

                        <div className="space-x-0.5">
                            <span>Type:</span>
                            <span>{formRef.current?.getValues().type}</span>
                        </div>

                        <div className="space-x-0.5">
                            <span>Unit:</span>
                            <span>{formRef.current?.getValues().unit}</span>
                        </div>

                        <div className="space-x-0.5">
                            <span>Expiry:</span>
                            <span>
                                {formRef.current?.getValues().expiry_date &&
                                    format(
                                        formRef.current?.getValues()
                                            .expiry_date ?? '',
                                        'dd/MM/yyyy',
                                    )}
                            </span>
                        </div>
                    </div>

                    <OrderTypeSelector />
                </div>

                <IwForm
                    schema={AddProductFormSchema}
                    initialValues={initialValue}
                    onSubmit={handleAddProductToCart}
                    ref={formRef}
                >
                    <div className="mt-2 grid grid-cols-4 gap-2">
                        <TextField<AddProductFormType>
                            name="quantity"
                            label={`Quantity ${availableQuantity !== null ? `(${availableQuantity})` : ''}`}
                            ref={quantityInputRef}
                        />
                        <TextField<AddProductFormType>
                            name="price"
                            label="Price"
                        />
                        <div
                            className={
                                'flex flex-col items-start justify-between'
                            }
                        >
                            <p>Discount</p>

                            <div className={'flex items-center justify-center'}>
                                <SelectField<AddProductFormType>
                                    name={'discount_type'}
                                    className={'w-[80px] min-w-[40px]'}
                                    options={[
                                        {
                                            label: 'Tk',
                                            value: DiscountType.Fixed,
                                        },
                                        {
                                            label: '%',
                                            value: DiscountType.Percentage,
                                        },
                                    ]}
                                />

                                <TextField<AddProductFormType> name="discount_value" />
                            </div>
                        </div>
                        <Button
                            className="self-end"
                            type="submit"
                            disabled={isAddItemToCartLoading}
                        >
                            {isAddItemToCartLoading ? (
                                <Loader2Icon className="animate-spin" />
                            ) : (
                                'Add Product'
                            )}
                        </Button>
                    </div>
                </IwForm>
            </CardContent>
        </Card>
    );
};

ProductSelector.displayName = 'ProductSelector';
